import 'rsuite/dist/rsuite.min.css';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Skeleton,
  Divider,
  Select,
  SelectItem,
  Card,
  CardBody,
  Button,
  Checkbox,
  CheckboxGroup,
  Tabs,
  Tab,
  Tooltip,
} from '@nextui-org/react';
import VarietyEvolutionChart from '../../components/charts/VarietyEvolutionChart';
import CaliberEvolutionChart from '../../components/charts/CaliberEvolutionChart';
import BoxFormatEvolutionChart from '../../components/charts/BoxFormatEvolutionChart';
import FleetEvolutionChart from '../../components/charts/FleetEvolutionChart';
import useExchangeRates from '../../hooks/useExchangeRates';
import ModalSlideFilter from '../../components/filters/modal-sidebar-filter';
import RegisterPricesModal from '../../components/market/RegisterPricesModal';
import { listBitacoraPrices, BitacoraPriceData } from 'common/src/api/market_comments';
import { DateRangePicker } from 'rsuite';
import MarketsEvolutionChart from '../../components/charts/MarketsEvolutionChart';
import ColorEvolutionChart from '../../components/charts/ColorEvolutionChart';
import type { BitacoraMode } from '../../components/charts/bitacoraSeries';
import useMarketData from 'common/src/hooks/useMarketData';
import useMarketDateSync from 'common/src/hooks/useMarketDateSync';
import { MarketData } from 'common/src/utils/types';
import InformationTooltip from '../../components/ui/InformationTooltip';
import {
  Alert,
  FiltersToggle,
  PageHeader,
  PageLayout,
} from '../../components/ui';
import { logUserAction } from '../../../../admin/src/services/actionsService';
import useDataStore from 'common/src/stores/useDataStore';
import UnitPriceFilter from 'common/src/components/filters/UnitPriceFilter';
import useFilterStore from '../../stores/useFiltersStore';
import { SEASONS, DEFAULT_ORIGIN } from 'common/src/utils/definitions';
import PriceLevelFilter from 'common/src/components/filters/PriceLevelFilter';
import MarketCommissionsModal from '../../modals/MarketCommissionsModal';
import {
  loadMarketDataCommissions,
  saveMarketDataCommissions,
  MarketDataCommissions,
} from 'common/src/api/presettlement-settings';
import { toast } from 'react-toastify';
import useUserStore from 'common/src/stores/useUserStore';
import { useTranslation } from '../../hooks/useTranslation';

const CHART_TABS = {
  caliber: 'Por Calibre',
  variety: 'Por Variedad',
  format: 'Por Formato',
  market: 'Por Mercado',
  color: 'Por Color',
  fleet: 'Por Tipo de Flete',
} as const;

type ChartTab = keyof typeof CHART_TABS;

interface MarketEvolutionProps {
  forceSpeciesId?: number; // Force a specific species id (e.g., 2 for cherries in importers app)
  hideExporterPrice?: boolean; // Hide "Mostrar precios exportadora" checkbox
}

const MarketEvolution = ({
  forceSpeciesId,
  hideExporterPrice = false,
}: MarketEvolutionProps) => {
  const { t } = useTranslation('market');
  const { session } = useUserStore();
  const { userProfile } = useUserStore();
  const { selectedSeason } = useFilterStore();
  const hasDemo = userProfile?.exporter?.hasDemo;
  const DEMO_MIN_DATE_STR = '2024-12-15';
  const DEMO_MAX_DATE_STR = '2025-01-01';
  const [selectedTab, setSelectedTab] = useState<ChartTab>('caliber');

  const [selectedCurrency, setSelectedCurrency] = useState<'RMB' | 'USD'>(
    'RMB'
  );
  const [priceView, setPriceView] = useState<string>('kg');
  const [boxSizes, setBoxSizes] = useState<number[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(
    // En pantallas chicas los filtros son un panel que tapa la página: se
    // arranca cerrado para que "Registrar precios" quede a mano.
    () =>
      typeof window === 'undefined' ||
      window.matchMedia('(min-width: 768px)').matches
  );
  const [showPastsSeasons, setShowPastsSeasons] = useState(false);
  const [showExporterPrices, setShowExporterPrices] = useState(false);
  const [chartViewMode, setChartViewMode] = useState<'day' | 'week'>('day');

  const [viewMode, setViewMode] = useState<'chinese' | 'normal'>('chinese');
  const [visualization, setVisualization] = useState<'chart' | 'table'>(
    'chart'
  );
  const [isCommissionsModalOpen, setIsCommissionsModalOpen] = useState(false);
  const [commissionsSettings, setCommissionsSettings] =
    useState<MarketDataCommissions | null>(null);
  const [commissionsReloadTrigger, setCommissionsReloadTrigger] = useState(0);
  const [isRegisterPricesOpen, setIsRegisterPricesOpen] = useState(false);
  // Puntos de Bitácora: un promedio por fecha, o uno por cliente o por etiqueta.
  const [bitacoraMode, setBitacoraMode] = useState<BitacoraMode>('average');
  const [selectedOrigins, setSelectedOrigins] = useState<string[]>([DEFAULT_ORIGIN]);
  const [bitacoraData, setBitacoraData] = useState<BitacoraPriceData[]>([]);
  const [bitacoraReloadTrigger, setBitacoraReloadTrigger] = useState(0);
  const {
    selectedSeasonsMarketData,
    setSelectedSeasonsMarketData,
    selectedSpecies,
    loadingMarketData,
    priceLevel,
    allMarketData,
  } = useDataStore();

  // Use forceSpeciesId if provided, otherwise use selectedSpecies from store
  const effectiveSpeciesId = forceSpeciesId ?? selectedSpecies?.id;
  const isCherry = effectiveSpeciesId === 2;

  // Force weekly chart view for non-cherry species
  useEffect(() => {
    if (!isCherry) {
      setChartViewMode('week');
    }
  }, [isCherry]);

  // Force USD when priceLevel is not 'market', reset to RMB when back to 'market'
  useEffect(() => {
    if (priceLevel !== 'market') {
      setSelectedCurrency('USD');
    } else {
      setSelectedCurrency('RMB');
    }
  }, [priceLevel]);

  useEffect(() => {
    logUserAction('view', 'page', 'evolution-market');
  }, []);

  // Load commission settings when species changes
  useEffect(() => {
    const loadSettings = async () => {
      if (!selectedSpecies?.id || !userProfile?.exporter?.id || !session)
        return;

      const settings = await loadMarketDataCommissions(
        userProfile.exporter.id,
        selectedSpecies.id,
        session
      );
      setCommissionsSettings(settings);
    };

    loadSettings();
  }, [selectedSpecies?.id, userProfile?.exporter?.id]);

  // Check if we need to open modal when price level changes
  const handlePriceLevelChange = useCallback(
    (newLevel: typeof priceLevel) => {
      if (newLevel !== 'market' && !commissionsSettings) {
        // Need to configure settings first
        setIsCommissionsModalOpen(true);
      }
    },
    [commissionsSettings]
  );

  // Save commission settings
  const handleSaveCommissions = async (
    commissions: MarketDataCommissions
  ): Promise<boolean> => {
    if (!userProfile?.exporter?.id || !selectedSpecies?.id || !session) {
      toast.error(t('Error: Datos de usuario o especie no disponibles'));
      return false;
    }

    const success = await saveMarketDataCommissions(
      userProfile.exporter.id,
      selectedSpecies.id,
      commissions,
      session
    );

    if (success) {
      setCommissionsSettings(commissions);
      // Trigger reload of commissions in hook
      setCommissionsReloadTrigger((prev) => prev + 1);
      toast.success(t('Configuración guardada exitosamente'));
      return true;
    } else {
      toast.error(t('Error al guardar la configuración'));
      return false;
    }
  };

  // ✅ NUEVO: Hook centralizado que maneja todas las fechas y elimina competing useEffects
  const {
    selectedDate,
    dateRange,
    isLoading: loadingSelectedDate,
    updateDateRange,
    setDateRange,
  } = useMarketDateSync({
    speciesId: effectiveSpeciesId,
    season: selectedSeason,
    hasDemo,
    viewMode: 'week',
    demoDefaultDate: '2024-12-20',
    daysBack: 15, // 15 días para evolution
    origins: selectedOrigins, // ancla el rango a la última fecha con data de esos orígenes
  });

  // Extraer startDate y endDate del dateRange
  const startDate = dateRange[0].toISOString().split('T')[0];
  const endDate = dateRange[1].toISOString().split('T')[0];

  const { marketData, loading } = useMarketData({
    viewMode: 'week',
    selectedDate: undefined,
    dateRange,
    enabled: !loadingSelectedDate,
    reloadTrigger: commissionsReloadTrigger,
    forceSpeciesId,
  });

  // Exchange rates for dynamic currency conversion
  // When comparing seasons, fetch ALL rates to apply per-date conversion
  const {
    rate: exchangeRate,
    rateInfo: exchangeRateInfo,
    isDefault: isDefaultRate,
    getRateForDate,
  } = useExchangeRates({
    dateRange,
    currency: 'RMB',
    enabled: !loadingSelectedDate,
    fetchAll: showPastsSeasons, // Load all rates when comparing seasons
  });

  // Dynamic RMB to USD conversion using average exchange rate (for current range)
  const rmbToUsd = useCallback(
    (rmb: number): number => {
      return rmb / exchangeRate;
    },
    [exchangeRate]
  );

  // Per-date RMB to USD conversion (for season comparison - uses actual rate for each date)
  const rmbToUsdForDate = useCallback(
    (rmb: number, dateStr: string): number => {
      const rate = getRateForDate(dateStr);
      return rmb / rate;
    },
    [getRateForDate]
  );

  // Per-date USD to RMB conversion (for season comparison)
  const usdToRmbForDate = useCallback(
    (usd: number, dateStr: string): number => {
      const rate = getRateForDate(dateStr);
      return usd * rate;
    },
    [getRateForDate]
  );

  // Load bitácora prices for chart overlay
  useEffect(() => {
    let cancelled = false;
    async function loadBitacora() {
      try {
        const params: any = {};
        if (dateRange) {
          const [from, to] = dateRange;
          params.from = from.toISOString().split('T')[0];
          params.to = to.toISOString().split('T')[0];
        }
        if (effectiveSpeciesId) params.species_id = effectiveSpeciesId;
        if (selectedSeason) params.season = selectedSeason;

        const data = await listBitacoraPrices(params);
        if (!cancelled) setBitacoraData(data);
      } catch {
        if (!cancelled) setBitacoraData([]);
      }
    }
    loadBitacora();
    return () => {
      cancelled = true;
    };
  }, [
    dateRange?.[0]?.toISOString?.(),
    dateRange?.[1]?.toISOString?.(),
    effectiveSpeciesId,
    selectedSeason,
    bitacoraReloadTrigger, // Reload when bitácora is edited
  ]);

  useEffect(() => {
    if (showPastsSeasons) {
      setSelectedSeasonsMarketData(selectedSeasonsMarketData);
      setChartViewMode('day'); // Force day view for season comparison
    }
  }, [showPastsSeasons]);

  // ✅ Handle species default_unit_price (mantener este effect, no compite con fechas)
  useEffect(() => {
    if (selectedSpecies?.id === undefined) return;

    if (selectedSpecies?.default_unit_price) {
      const defaultUnitPrice = selectedSpecies.default_unit_price;
      setPriceView(defaultUnitPrice === 1 ? 'kg' : `box-${defaultUnitPrice}`);

      if (defaultUnitPrice > 1) {
        setBoxSizes([defaultUnitPrice]);
      } else {
        setBoxSizes([]);
      }
    } else {
      setPriceView('kg');
      setBoxSizes([]);
    }
  }, [selectedSpecies?.id, selectedSpecies?.default_unit_price]);

  // ✅ OPTIMIZACIÓN: Memoizar handleDateRangeChange
  const handleDateRangeChange = useCallback(
    (value: [Date, Date] | null) => {
      if (!value) return;
      let [start, end] = value;

      // Clamp to demo range if applicable
      if (hasDemo) {
        const minDate = new Date(`${DEMO_MIN_DATE_STR}T12:00:00.000Z`);
        const maxDate = new Date(`${DEMO_MAX_DATE_STR}T12:00:00.000Z`);
        if (start < minDate) start = new Date(minDate);
        if (start > maxDate) start = new Date(maxDate);
        if (end < minDate) end = new Date(minDate);
        if (end > maxDate) end = new Date(maxDate);
        if (end < start) end = new Date(start);
      }

      // Usar función del hook
      start.setHours(12, 0, 0, 0);
      end.setHours(12, 0, 0, 0);
      updateDateRange([start, end]);
    },
    [updateDateRange, hasDemo, DEMO_MIN_DATE_STR, DEMO_MAX_DATE_STR]
  );

  // ✅ OPTIMIZACIÓN: Function to update prices for data that comes FROM THE HOOK
  // (already has FOB/Producer calculations applied, just needs currency/multiplier)
  // Always uses per-date exchange rate, never the average
  const updatePricesFromHook = useCallback(
    (data: MarketData[]) => {
      return data.map((item) => {
        let price = item.price_kg;

        // marketData from hook already has FOB/Producer calculations
        // Only apply currency conversion if in 'market' mode
        if (priceLevel === 'market') {
          // Market prices come in RMB, convert to USD if needed
          // Always use per-date conversion with the item's specific date
          if (selectedCurrency === 'USD') {
            price = rmbToUsdForDate(price, item.date);
          }
          // If currency is RMB, keep as is
        }
        // If priceLevel is not 'market', price is already in USD from hook

        // Apply box multiplier
        const multiplier =
          priceView === 'kg' ? 1 : parseInt(priceView.split('-')[1]);
        price *= multiplier;

        return {
          ...item,
          price_kg: price,
          price_display: price.toFixed(2),
        };
      });
    },
    [selectedCurrency, priceView, priceLevel, rmbToUsdForDate]
  );

  // ✅ Function to update prices for RAW DATA (allMarketData from store)
  // This applies FOB/Producer calculations ONLY
  // ChineseChart and other charts will handle currency/multiplier themselves
  // When comparing seasons, uses per-date exchange rates for accurate historical conversion
  const updatePricesFromRaw = useCallback(
    (data: MarketData[]) => {
      return data.map((item) => {
        let price = item.price_kg;
        const itemDate = item.date; // Get the date for this specific item

        // Apply FOB/Producer calculations if needed
        // These calculations convert to USD and return price in USD/kg
        if (priceLevel !== 'market' && commissionsSettings) {
          // Convert to USD/Kg using the exchange rate for this item's date
          let priceUsdPerKg = rmbToUsdForDate(item.price_kg, itemDate);

          // Determine freight costs based on transport type
          const isAir = item.transport === 'AIR';
          const freightCost = isAir
            ? commissionsSettings.air_freight_cost
            : commissionsSettings.ocean_freight_cost;
          const otherCosts = isAir
            ? commissionsSettings.air_other_costs
            : commissionsSettings.ocean_other_costs;

          // Calculate FOB: Price - Receiver Commission - VAT - Freight - Other Costs
          const receiverCommission =
            priceUsdPerKg * (commissionsSettings.receiver_commission / 100);
          const vat = priceUsdPerKg * (commissionsSettings.vat_tax / 100);
          const fobPrice =
            priceUsdPerKg - receiverCommission - vat - freightCost - otherCosts;

          if (priceLevel === 'fob') {
            price = Math.max(0, fobPrice);
          } else if (priceLevel === 'producer') {
            // Calculate Producer Return: FOB - Exporter Commission - Packing Cost
            const exporterCommission =
              fobPrice * (commissionsSettings.exporter_commission / 100);
            const producerReturn =
              fobPrice - exporterCommission - commissionsSettings.packing_cost;
            price = Math.max(0, producerReturn);
          }

          // Now convert back to RMB so ChineseChart can handle conversions
          // ChineseChart expects data in RMB and does its own currency conversion
          // Use the same date's exchange rate for consistency
          price = usdToRmbForDate(price, itemDate);
        }
        // If priceLevel is 'market', keep price as is (in RMB)

        // NOTE: We don't apply currency conversion or box multiplier here
        // because ChineseChart and other chart components handle that themselves

        return {
          ...item,
          price_kg: price,
          price_display: price.toFixed(2),
        };
      });
    },
    [priceLevel, commissionsSettings, rmbToUsdForDate, usdToRmbForDate]
  );

  // ✅ OPTIMIZACIÓN: useMemo para updatedMarketData (from hook - already processed)
  const updatedMarketDataAllOrigins = useMemo(
    () => updatePricesFromHook(marketData),
    [updatePricesFromHook, marketData]
  );

  // ✅ Apply full price calculations to allMarketData (raw data from store)
  const updatedAllMarketDataAllOrigins = useMemo(
    () => updatePricesFromRaw(allMarketData),
    [updatePricesFromRaw, allMarketData]
  );

  // Orígenes disponibles.
  //
  // OJO: no se puede usar solo `allMarketData` — hoy queda siempre vacío por un
  // bug preexistente en routes/index.tsx (`!allMarketData` con un array vacío
  // es siempre false, así que fetchAllMarketData() nunca corre). Y `marketData`
  // está acotado al rango de fechas, así que en plena temporada chilena trae
  // solo CHILE.
  //
  // Se unen ambas fuentes más lo que esté seleccionado, para que la opción
  // elegida nunca desaparezca de la lista y el usuario pueda volver atrás.
  const availableOrigins = useMemo(() => {
    const found = new Set<string>();
    for (const item of [...allMarketData, ...marketData] as {
      origin?: string | null;
    }[]) {
      if (item.origin) found.add(item.origin);
    }
    for (const origin of selectedOrigins) found.add(origin);
    return Array.from(found).sort((a, b) => a.localeCompare(b));
  }, [allMarketData, marketData, selectedOrigins]);

  // Por defecto se muestra solo Chile: hay semanas del año (24-31) sin ninguna
  // fruta chilena, y la del hemisferio norte se cotiza al doble, así que
  // mezclarlas deforma la curva. Ojo: no todas las especies tienen data
  // chilena (palta y naranja no tienen ninguna), así que si no existe para la
  // especie seleccionada se cae a "todos" en vez de dejar el gráfico vacío.
  // Nota: el default se deja fijo en DEFAULT_ORIGIN. Si la especie no tiene
  // data de ese origen, useMarketDateSync re-ancla el rango sin filtrar, así
  // que no hace falta corregirlo acá (y hacerlo con una lista incompleta
  // provocaba que se reseteara la selección del usuario).

  const updatedMarketData = useMemo(
    () =>
      selectedOrigins.length === 0
        ? updatedMarketDataAllOrigins
        : updatedMarketDataAllOrigins.filter(
            (item: { origin?: string | null }) =>
              !!item.origin && selectedOrigins.includes(item.origin)
          ),
    [updatedMarketDataAllOrigins, selectedOrigins]
  );

  const updatedAllMarketData = useMemo(
    () =>
      selectedOrigins.length === 0
        ? updatedAllMarketDataAllOrigins
        : updatedAllMarketDataAllOrigins.filter(
            (item: { origin?: string | null }) =>
              !!item.origin && selectedOrigins.includes(item.origin)
          ),
    [updatedAllMarketDataAllOrigins, selectedOrigins]
  );

  // ✅ Transform bitácora data with same price conversion as market data
  // Uses per-date exchange rates for accurate conversion
  const transformedBitacoraData = useMemo(() => {
    if (bitacoraData.length === 0) return [];

    // Get multiplier from priceView value (either 1 for 'kg' or the number after 'box-')
    const multiplier =
      priceView === 'kg' ? 1 : parseInt(priceView.split('-')[1]);

    return bitacoraData.map((item) => {
      const itemDate = item.date_from; // Use the bitácora entry date for exchange rate lookup
      // unit_price / unit_weight en la moneda en que se registró. Lo que sigue
      // asume RMB, así que un registro en dólares se pasa a RMB primero.
      let price =
        item.local_currency === 'USD'
          ? item.price_kg * getRateForDate(itemDate)
          : item.price_kg;

      // Apply same price level calculation as market data
      if (priceLevel === 'market') {
        // Market mode: just convert currency if needed
        if (selectedCurrency === 'USD') {
          // Use per-date conversion for accurate historical rates
          price = rmbToUsdForDate(price, itemDate);
        }
      } else if (commissionsSettings) {
        // FOB or Producer mode: apply commission calculations
        // First convert to USD using the exchange rate for this item's date
        let priceUsdPerKg = rmbToUsdForDate(price, itemDate);

        // Use fleet if specified, otherwise default to ocean (M = Maritimo)
        const isAir = item.fleet === 'A';
        const freightCost = isAir
          ? commissionsSettings.air_freight_cost
          : commissionsSettings.ocean_freight_cost;
        const otherCosts = isAir
          ? commissionsSettings.air_other_costs
          : commissionsSettings.ocean_other_costs;

        // Calculate FOB: Price - Receiver Commission - VAT - Freight - Other Costs
        const receiverCommission =
          priceUsdPerKg * (commissionsSettings.receiver_commission / 100);
        const vat = priceUsdPerKg * (commissionsSettings.vat_tax / 100);
        const fobPrice =
          priceUsdPerKg - receiverCommission - vat - freightCost - otherCosts;

        if (priceLevel === 'fob') {
          price = Math.max(0, fobPrice);
        } else {
          // Producer level
          const exporterCommission =
            fobPrice * (commissionsSettings.exporter_commission / 100);
          const producerReturn =
            fobPrice - exporterCommission - commissionsSettings.packing_cost;
          price = Math.max(0, producerReturn);
        }
      }

      // Apply box multiplier
      price *= multiplier;

      return {
        ...item,
        price_kg: price,
      };
    });
  }, [bitacoraData, priceView, selectedCurrency, priceLevel, commissionsSettings, rmbToUsdForDate, getRateForDate]);

  const renderChart = () => {
    // Si no está en modo "VS temporadas pasadas", forzar vista normal
    const effectiveViewMode = showPastsSeasons ? viewMode : 'normal';

    switch (selectedTab) {
      case 'caliber':
        return (
          <CaliberEvolutionChart
            data={updatedMarketData}
            allMarketData={updatedAllMarketData}
            selectedCurrency={selectedCurrency}
            priceView={priceView.startsWith('box') ? 'box' : 'kg'}
            rangeSelected={{ startDate, endDate }}
            showPastsSeasons={showPastsSeasons}
            showExporterPrices={showExporterPrices}
            viewMode={effectiveViewMode}
            chartViewMode={chartViewMode}
            visualization={visualization}
            bitacoraData={transformedBitacoraData}
            bitacoraMode={bitacoraMode}
          />
        );
      case 'variety':
        return (
          <VarietyEvolutionChart
            data={updatedMarketData}
            allMarketData={updatedAllMarketData}
            selectedCurrency={selectedCurrency}
            priceView={priceView.startsWith('box') ? 'box' : 'kg'}
            rangeSelected={{ startDate, endDate }}
            showPastsSeasons={showPastsSeasons}
            showExporterPrices={showExporterPrices}
            viewMode={effectiveViewMode}
            chartViewMode={chartViewMode}
            visualization={visualization}
            bitacoraData={transformedBitacoraData}
            bitacoraMode={bitacoraMode}
          />
        );
      case 'format':
        return (
          <BoxFormatEvolutionChart
            data={updatedMarketData}
            allMarketData={updatedAllMarketData}
            selectedCurrency={selectedCurrency}
            priceView={priceView.startsWith('box') ? 'box' : 'kg'}
            rangeSelected={{ startDate, endDate }}
            showPastsSeasons={showPastsSeasons}
            showExporterPrices={showExporterPrices}
            viewMode={effectiveViewMode}
            chartViewMode={chartViewMode}
            visualization={visualization}
            bitacoraData={transformedBitacoraData}
            bitacoraMode={bitacoraMode}
          />
        );
      case 'market':
        return (
          <MarketsEvolutionChart
            data={updatedMarketData}
            allMarketData={updatedAllMarketData}
            selectedCurrency={selectedCurrency}
            rangeSelected={{ startDate, endDate }}
            showPastsSeasons={showPastsSeasons}
            priceView={priceView.startsWith('box') ? 'box' : 'kg'}
            showExporterPrices={showExporterPrices}
            viewMode={effectiveViewMode}
            chartViewMode={chartViewMode}
            visualization={visualization}
            bitacoraData={transformedBitacoraData}
            bitacoraMode={bitacoraMode}
          />
        );
      case 'color':
        return (
          <ColorEvolutionChart
            data={updatedMarketData}
            allMarketData={updatedAllMarketData}
            selectedCurrency={selectedCurrency}
            rangeSelected={{ startDate, endDate }}
            showPastsSeasons={showPastsSeasons}
            priceView={priceView.startsWith('box') ? 'box' : 'kg'}
            showExporterPrices={showExporterPrices}
            viewMode={effectiveViewMode}
            chartViewMode={chartViewMode}
            visualization={visualization}
            bitacoraData={transformedBitacoraData}
            bitacoraMode={bitacoraMode}
          />
        );
      case 'fleet':
        return (
          <FleetEvolutionChart
            data={updatedMarketData}
            allMarketData={updatedAllMarketData}
            selectedCurrency={selectedCurrency}
            rangeSelected={{ startDate, endDate }}
            showPastsSeasons={showPastsSeasons}
            priceView={priceView.startsWith('box') ? 'box' : 'kg'}
            showExporterPrices={showExporterPrices}
            viewMode={effectiveViewMode}
            chartViewMode={chartViewMode}
            visualization={visualization}
            bitacoraData={transformedBitacoraData}
            bitacoraMode={bitacoraMode}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {/* <FullScreenLoading 
        isOpen={loadingMarketData || loading}
        message="Cargando datos del mercado..."
      /> */}
      <div
        className='flex w-full h-screen overflow-x-hidden'
      >
        <ModalSlideFilter
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
        >
          <div className='bg-background p-4 pt-8 h-full overflow-y-auto'>
            <div className='space-y-4'>
              {hasDemo && (
                <Alert className='mb-4'>
                  {t('Contrata CherryMarket para habilitar todas las opciones.')}
                </Alert>
              )}

              <p className='text-sm text-default-500 mb-1'>
                {t('Rango de Fechas')}
              </p>
              <div className='relative z-[100]'>
                <div className='relative'>
                  <DateRangePicker
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    hoverRange={!isCherry ? 'week' : undefined}
                    showWeekNumbers
                    showOneCalendar
                    placeholder={!isCherry ? t('Seleccionar semana') : t('Seleccionar rango de fechas')}
                    character=' - '
                    format='dd-MM-yyyy'
                    className='w-full'
                    isoWeek={true}
                    menuStyle={{ zIndex: 9999 }}
                    disabled={
                      showPastsSeasons ||
                      loadingMarketData ||
                      loading ||
                      loadingSelectedDate
                    }
                    disabledDate={(date) => {
                      if (!hasDemo) return false;
                      const minDate = new Date(
                        `${DEMO_MIN_DATE_STR}T00:00:00.000Z`
                      );
                      const maxDate = new Date(
                        `${DEMO_MAX_DATE_STR}T23:59:59.999Z`
                      );
                      return date < minDate || date > maxDate;
                    }}
                    ranges={[
                      {
                        label: t('Diciembre 2023'),
                        value: [
                          new Date('2023-12-01T12:00:00'),
                          new Date('2023-12-31T12:00:00'),
                        ],
                      },
                    ]}
                    locale={{
                      sunday: 'Do',
                      monday: 'Lu',
                      tuesday: 'Ma',
                      wednesday: 'Mi',
                      thursday: 'Ju',
                      friday: 'Vi',
                      saturday: 'Sa',
                      ok: t('Aplicar'),
                      today: t('Hoy'),
                      yesterday: t('Ayer'),
                      last7Days: t('Últimos 7 días'),
                    }}
                  />
                  {hasDemo && (
                    <div className='mt-2 text-xs text-blue-600'>
                      {t('Rango disponible en demo:')} {DEMO_MIN_DATE_STR}{' '}
                      {t('a')} {DEMO_MAX_DATE_STR}
                    </div>
                  )}
                  {showPastsSeasons && (
                    <div className='mt-2 text-xs text-default-400 italic'>
                      {t(
                        'El rango de fechas se deshabilita al comparar temporadas'
                      )}
                    </div>
                  )}
                </div>
              </div>
              <Divider />
              <p className='text-sm text-default-500 mb-1'>{t('Visualización')}</p>
              {isCherry && (
              <Tabs
                fullWidth
                size='sm'
                aria-label={t('Visualización')}
                className='mb-2'
                selectedKey={chartViewMode}
                onSelectionChange={(k) => setChartViewMode(k as 'day' | 'week')}
                isDisabled={showPastsSeasons}
              >
                <Tab key='day' title={t('Día')} />
                <Tab key='week' title={t('Semana')} />
              </Tabs>
              )}
              <Tabs
                fullWidth
                size='sm'
                aria-label={t('Tipo de visualización')}
                className='mb-2'
                selectedKey={visualization}
                onSelectionChange={(k) => setVisualization(k as 'chart' | 'table')}
              >
                <Tab key='chart' title={t('Gráficos')} />
                <Tab key='table' title={t('Tablas')} />
              </Tabs>
              <div className='space-y-1'>
                <PriceLevelFilter
                  className='w-full'
                  onLevelChange={handlePriceLevelChange}
                />
                {priceLevel !== 'market' && (
                  <button
                    onClick={() => setIsCommissionsModalOpen(true)}
                    className='text-xs text-default-600 hover:text-default-700 underline'
                  >
                    {t('Editar configuración')}
                  </button>
                )}
              </div>

              <Divider />
              <p className='text-sm text-default-500 mb-1'>{t('Unidades')}</p>
              <Select
                label={t('Moneda')}
                selectedKeys={[selectedCurrency]}
                onChange={(e) =>
                  setSelectedCurrency(e.target.value as 'RMB' | 'USD')
                }
                className='w-full'
                aria-label={t('Moneda')}
                isDisabled={priceLevel !== 'market'}
                description={
                  priceLevel !== 'market'
                    ? t('Los cálculos FOB/Productor se muestran en USD')
                    : undefined
                }
              >
                <SelectItem key='RMB' value='RMB'>
                  RMB ¥
                </SelectItem>
                <SelectItem key='USD' value='USD'>
                  USD $
                </SelectItem>
              </Select>
              {selectedCurrency === 'USD' && (
                <div className='text-xs text-default-400 -mt-2 px-1 flex items-center gap-1'>
                  <span className='font-medium'>{t('Tipo de cambio')}:</span>{' '}
                  <span className={isDefaultRate ? 'text-amber-600' : 'text-green-600'}>
                    {exchangeRateInfo}
                  </span>
                  <InformationTooltip size={14}>
                    <p className='text-sm font-semibold mb-1'>
                      {t('Conversión USD/RMB')}
                    </p>
                    <p className='text-xs'>
                      {t('A cada día del rango se le aplica su tipo de cambio correspondiente. Solo se muestra el promedio como referencia.')}
                    </p>
                  </InformationTooltip>
                </div>
              )}
              <UnitPriceFilter
                selectedSpecies={selectedSpecies}
                value={priceView}
                onChange={setPriceView}
                className='w-full'
              />
              {availableOrigins.length > 0 && (
                <Select
                  label={t('Origen')}
                  size='sm'
                  className='w-full'
                  selectionMode='multiple'
                  selectedKeys={new Set(selectedOrigins)}
                  onSelectionChange={(keys) => {
                    const selected = Array.from(keys) as string[];
                    // La opción 'all' actúa como atajo: si ya hay algo elegido
                    // limpia, si no selecciona todo. Mismo comportamiento que
                    // los demás filtros múltiples (ver CaliberFilter).
                    if (selected.includes('all')) {
                      setSelectedOrigins(
                        selectedOrigins.length > 0 ? [] : availableOrigins
                      );
                      return;
                    }
                    setSelectedOrigins(selected);
                  }}
                >
                  {[
                    {
                      key: 'all',
                      label:
                        selectedOrigins.length > 0
                          ? t('Deseleccionar todos')
                          : t('Seleccionar todos'),
                    },
                    ...availableOrigins.map((origin) => ({
                      key: origin,
                      label: origin,
                    })),
                  ].map((option) => (
                    <SelectItem key={option.key} value={option.key}>
                      {option.label}
                    </SelectItem>
                  ))}
                </Select>
              )}
              <Divider />
              <p className='text-sm text-default-500 mb-1'>{t('Bitácora')}</p>
              <Select
                label={t('Puntos de Bitácora')}
                selectedKeys={[bitacoraMode]}
                onSelectionChange={(keys) =>
                  setBitacoraMode(
                    (Array.from(keys)[0] as BitacoraMode) || 'average'
                  )
                }
                className='w-full'
                isDisabled={hasDemo}
              >
                <SelectItem key='average' value='average'>
                  {t('Promedio')}
                </SelectItem>
                <SelectItem key='client' value='client'>
                  {t('Por cliente')}
                </SelectItem>
                <SelectItem key='label' value='label'>
                  {t('Por etiqueta')}
                </SelectItem>
              </Select>

              <Divider />
              <p className='text-sm text-default-500 mb-1'>{t('Incluir')}</p>

              <div className='flex flex-col items-start gap-2'>
                {!hideExporterPrice && (
                  <Checkbox
                    isDisabled={hasDemo}
                    size='sm'
                    onChange={(e) => setShowExporterPrices(e.target.checked)}
                  >
                    {t('Precios exportadora')}
                  </Checkbox>
                )}
                <Checkbox
                  isDisabled={hasDemo}
                  size='sm'
                  onChange={(e) => setShowPastsSeasons(e.target.checked)}
                >
                  {t('Otras temporadas')}
                </Checkbox>
              </div>

              {showPastsSeasons && (
                <div className='w-full mt-4'>
                  <Select
                    label={t('Temporadas para comparar')}
                    description={`${t('Temporada seleccionada:')} ${selectedSeason}`}
                    selectionMode='multiple'
                    selectedKeys={new Set(selectedSeasonsMarketData)}
                    onSelectionChange={(keys) => {
                      const selectedKeys = Array.from(keys as Set<string>);
                      // Always include selected season
                      if (!selectedKeys.includes(selectedSeason)) {
                        selectedKeys.push(selectedSeason);
                      }
                      setSelectedSeasonsMarketData(selectedKeys);
                    }}
                    isDisabled={hasDemo || loadingMarketData}
                    className='w-full'
                    renderValue={(items) => {
                      const itemsArray = Array.from(items);
                      const displayCount = 2;
                      const remaining = itemsArray.length - displayCount;

                      return (
                        <div className='flex gap-2 items-center'>
                          {itemsArray.slice(0, displayCount).map((item) => (
                            <span key={item.key} className='text-sm'>
                              {item.textValue}
                            </span>
                          ))}
                          {remaining > 0 && (
                            <span className='text-sm text-default-400'>
                              +{remaining}
                            </span>
                          )}
                        </div>
                      );
                    }}
                  >
                    {Object.values(SEASONS).map((season) => (
                      <SelectItem
                        key={season.name}
                        value={season.name}
                        textValue={season.name}
                      >
                        {season.name}{' '}
                        {selectedSeason === season.name && t('(Seleccionada)')}
                      </SelectItem>
                    ))}
                  </Select>
                  <p className='text-xs text-default-400 mt-2'>
                    {t('La temporada seleccionada')} ({selectedSeason}){' '}
                    {t('siempre se muestra')}
                  </p>

                  <Divider className='my-4' />
                  <p className='text-sm text-default-500 mb-2'>
                    {t('Tipo de Comparación')}
                  </p>
                  <Tabs
                    fullWidth
                    size='sm'
                    aria-label={t('Tipo de Comparación')}
                    selectedKey={viewMode}
                    onSelectionChange={(k) =>
                      setViewMode(k as 'chinese' | 'normal')
                    }
                  >
                    <Tab key='normal' title={t('Sem vs Sem')} />
                    <Tab key='chinese' title={t('Año Nuevo Chino')} />
                  </Tabs>
                </div>
              )}
            </div>
          </div>
        </ModalSlideFilter>

        <PageLayout>
          <PageHeader
            title={t('Evolución del Mercado')}
            titleAddon={
              <InformationTooltip>
                <p className='text-sm font-semibold mb-2'>
                  {t('Evolución del Mercado')}
                </p>
                <ul className='list-disc ml-4 mt-1 text-sm space-y-2'>
                  <li>
                    <span className='font-medium'>
                      {t('Análisis Temporal:')}
                    </span>{' '}
                    {t(
                      'Visualiza la evolución de precios a lo largo del tiempo'
                    )}
                  </li>
                  <li>
                    <span className='font-medium'>{t('Gráficos:')}</span>
                    <ul className='list-circle ml-4 mt-1 space-y-1'>
                      <li>{t('Tendencias de precios por calibre')}</li>
                      <li>{t('Comparativa con temporadas anteriores')}</li>
                    </ul>
                  </li>
                  <li>
                    <span className='font-medium'>
                      {t('Filtros Disponibles:')}
                    </span>
                    <ul className='list-circle ml-4 mt-1 space-y-1'>
                      <li>{t('Rango de fechas personalizado')}</li>
                      <li>{t('Moneda (RMB/USD)')}</li>
                      <li>{t('Unidad (kg/caja)')}</li>
                      <li>{t('Comparativa con temporadas pasadas')}</li>
                    </ul>
                  </li>
                </ul>
              </InformationTooltip>
            }
            description={t(
              'Analiza la evolución de los precios del mercado a lo largo del tiempo.'
            )}
          />
          {hasDemo && (
            <Alert className='mb-4'>
              {t(
                'Estás viendo una versión de prueba con datos limitados. Contrata CherryMarket para cargar todos los datos y fechas.'
              )}
            </Alert>
          )}
          {/* Toolbar: filters + bitácora */}
          <div className='flex justify-between items-center mb-4'>
            <FiltersToggle
              isOpen={isFilterOpen}
              onToggle={() => setIsFilterOpen(!isFilterOpen)}
              label={isFilterOpen ? t('Ocultar filtros') : t('Mostrar filtros')}
            />
            <Button
              size='sm'
              color='primary'
              onPress={() => setIsRegisterPricesOpen(true)}
              isDisabled={hasDemo}
            >
              {t('Registrar precios')}
            </Button>
          </div>
          {loading ? (
            <div className='space-y-8 p-4'>
              {/* Cards */}
              <Card className='w-full overflow-hidden'>
                <CardBody className='p-6'>
                  <div className='space-y-6'>
                    {/* Chart Header */}
                    <div className='flex justify-between items-center'>
                      <div className='space-y-2'>
                        <Skeleton className='w-48 h-6 rounded-lg' />
                        <Skeleton className='w-72 h-4 rounded-lg' />
                      </div>
                      <div className='flex gap-2'>
                        <Skeleton className='w-24 h-8 rounded-lg' />
                        <Skeleton className='w-24 h-8 rounded-lg' />
                      </div>
                    </div>

                    {/* Chart Area */}
                    <div className='relative'>
                      <Skeleton className='w-full h-[400px] rounded-xl' />
                      {/* Chart Axis Hints */}
                      <div className='absolute bottom-0 left-0 right-0 flex justify-between px-4'>
                        {[...Array(7)].map((_, i) => (
                          <Skeleton key={i} className='w-12 h-4 rounded' />
                        ))}
                      </div>
                      <div className='absolute top-0 bottom-0 left-0 flex flex-col justify-between py-4'>
                        {[...Array(5)].map((_, i) => (
                          <Skeleton key={i} className='w-16 h-3 rounded' />
                        ))}
                      </div>
                    </div>

                    {/* Legend */}
                    <div className='flex gap-6 justify-center'>
                      {[...Array(3)].map((_, i) => (
                        <Skeleton key={i} className='w-24 h-4 rounded-lg' />
                      ))}
                    </div>
                  </div>
                </CardBody>
              </Card>
            </div>
          ) : (
            <div>
              <div className='overflow-x-auto'>
                <div className='min-w-[768px]'>
                  <Tabs
                    aria-label={t('Opciones de gráficos')}
                    selectedKey={selectedTab}
                    onSelectionChange={(key) => setSelectedTab(key as ChartTab)}
                    className='mb-4'
                    size='lg'
                    variant='underlined'
                  >
                    {Object.entries(CHART_TABS)
                      .filter(([key]) => {
                        if (key === 'color' && !selectedSpecies?.has_color) {
                          return false;
                        }
                        return true;
                      })
                      .map(([key, label]) => (
                        <Tab isDisabled={hasDemo} key={key} title={t(label)} />
                      ))}
                  </Tabs>
                </div>
              </div>
              {/* overflow-visible: el tooltip, con el detalle de Bitácora, puede
                  ser más alto que el gráfico y la Card de NextUI lo recortaba. */}
              <Card className='p-8 overflow-visible'>{renderChart()}</Card>
            </div>
          )}
        </PageLayout>
        {/* Registro rápido de precios de venta (Bitácora) */}
        <RegisterPricesModal
          isOpen={isRegisterPricesOpen}
          onClose={() => setIsRegisterPricesOpen(false)}
          onSaved={() => setBitacoraReloadTrigger((n) => n + 1)}
          exporterId={userProfile?.exporter?.id}
          userId={userProfile?.id}
          userName={[userProfile?.first_name, userProfile?.last_name]
            .filter(Boolean)
            .join(' ')}
          speciesId={effectiveSpeciesId}
          season={selectedSeason || undefined}
          varieties={selectedSpecies?.varieties || []}
          calibers={selectedSpecies?.calibers || []}
        />
      </div>

      {/* Modal de configuración de comisiones */}
      <MarketCommissionsModal
        isOpen={isCommissionsModalOpen}
        onClose={() => setIsCommissionsModalOpen(false)}
        onSave={handleSaveCommissions}
        initialValues={commissionsSettings || undefined}
      />
    </>
  );
};

export default MarketEvolution;
